import React, { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Hash, Loader2 } from 'lucide-react';

const dtBr = (s) => (s ? new Date(s).toLocaleString('pt-BR') : '—');

// Bloco "Numeração Safra": piso bancário + referência do sistema antigo.
// O campo manual é o ÚLTIMO número usado lá fora — nunca o próximo a atribuir.
export const NumeracaoSafra = ({ qtdNovos = 0, onChange }) => {
  const [info, setInfo] = useState(null);
  const [modo, setModo] = useState('automatico');
  const [valor, setValor] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const carregar = useCallback(async () => {
    try {
      const { data } = await api.get('/dnapay/safra/numeracao');
      setInfo(data);
      setModo(data.modo || 'automatico');
      onChange?.(data);
    } catch { /* bloco informativo */ }
  }, [onChange]);

  useEffect(() => { carregar(); }, [carregar]);

  const salvar = async () => {
    setBusy(true); setMsg(null);
    try {
      const { data } = await api.post('/dnapay/safra/numeracao/referencia-externa', {
        ultimo_numero_externo: valor, modo: 'manual',
      });
      setMsg(data.contador_preservado
        ? `Referência registrada. O DNACore já tinha numeração maior e o contador foi preservado: próximo ${data.proximo}.`
        : `Referência registrada. Próximo número: ${data.proximo}.`);
      setValor('');
      await carregar();
    } catch (e) {
      setMsg(e?.response?.data?.detail?.detail || 'Não foi possível salvar a referência.');
    }
    setBusy(false);
  };

  const prox = Number(info?.proximo || 0);
  const faixa = qtdNovos > 0 && prox
    ? `${String(prox).padStart(9, '0')} … ${String(prox + qtdNovos - 1).padStart(9, '0')}`
    : null;

  return (
    <div className="rounded border border-slate-200 bg-slate-50 p-3 space-y-2" data-testid="numeracao-safra">
      <div className="flex flex-wrap items-center gap-3">
        <span className="flex items-center gap-2 text-sm font-semibold text-slate-800">
          <Hash className="w-4 h-4" /> Numeração Safra
        </span>
        <div className="flex gap-1">
          {['automatico', 'manual'].map((m) => (
            <Button key={m} size="sm" variant={modo === m ? 'default' : 'outline'}
              onClick={() => setModo(m)} data-testid={`numeracao-modo-${m}`}>
              {m === 'automatico' ? 'Automático' : 'Manual'}
            </Button>
          ))}
          {modo === 'automatico' && <Badge variant="outline" className="self-center">padrão</Badge>}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-2 text-xs text-slate-700 md:grid-cols-3">
        <div>Último Nosso Número conhecido:{' '}
          <b data-testid="numeracao-ultimo">{info?.ultimo_conhecido || '—'}</b>
        </div>
        <div>Próximo número efetivo:{' '}
          <b className="text-emerald-700" data-testid="numeracao-proximo">{info?.proximo || '—'}</b>
        </div>
        <div>Referência externa:{' '}
          <b>{info?.referencia_externa?.valor || '—'}</b>
          {info?.referencia_externa?.data && (
            <span className="text-slate-500"> · {info.referencia_externa.origem} · {dtBr(info.referencia_externa.data)}</span>
          )}
        </div>
      </div>

      {faixa && (
        <div className="text-xs text-slate-700" data-testid="numeracao-faixa">
          Faixa prevista para os <b>{qtdNovos}</b> boletos novos: <b>{faixa}</b>{' '}
          <span className="text-slate-500">(números já atribuídos a boletos existentes são mantidos)</span>
        </div>
      )}

      {modo === 'manual' && (
        <div className="flex flex-wrap items-end gap-2 border-t pt-2">
          <div>
            <label className="block text-xs font-medium text-slate-700">
              Último Nosso Número gerado no outro sistema
            </label>
            <Input className="w-56 font-mono" value={valor} placeholder="000036944"
              onChange={(e) => setValor(e.target.value)} data-testid="numeracao-input" />
            <p className="text-[11px] text-slate-500">
              Informe o último utilizado; os novos boletos começarão no próximo disponível.
            </p>
          </div>
          <Button size="sm" onClick={salvar} disabled={busy || !valor.trim()}
            data-testid="numeracao-salvar">
            {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Salvar referência
          </Button>
        </div>
      )}

      {msg && <p className="text-xs text-blue-800" data-testid="numeracao-msg">{msg}</p>}
    </div>
  );
};
